<template>
  <div>
    <AppInputLabel v-if="label" class="mb-1" :name="name" :label="label" :hasError="hasError(name).value" :required="required" />
    <div :class="{ 'datepicker-right': rightAlign }" >
      <Datepicker
        ref="datepicker"
        :class="[
          { 'border-critial': hasError(name).value }
        ]"
        class="w-full custom-datepicker"
        v-model="innerValue"
        :minimumView="minimumView"
        :typeable="true"
        :lowerLimit="lowerLimit"
        :weekStartsOn="0"
        :format="format"
        :disabled="disabled"
        @blur="inputDate"
      />
    </div>
    <AppInputError v-show="hasError(name)" :error="errors.get(name)" />
  </div>
</template>
<script setup lang="ts">
import { inject, ref, watch, onBeforeUnmount } from "vue";
import Datepicker from "vue3-datepicker";
import { ValidationContext, ValidationContextKey } from "@/models/Context";
import useFormValidation from "@/modules/useFormValidation";
import { vClickOutside } from '@/directives/clickOutside';
import AppInputLabel from "./AppInputLabel.vue";
import AppInputError from "./AppInputError.vue";
import useDate from "@/modules/useDate";

const { getDate, formatISODate } = useDate();

const props = defineProps<{
  required: boolean;
  name: string;
  id?: string;
  label?: string;
  placeholder?: any;
  modelValue?: Date | null;
  lowerLimit?: Date;
  disabled?: boolean;
  minimumView: "time" | "day" | "month" | "year" | undefined;
  rightAlign?: boolean;
}>();

let innerValue = ref<Date | null | undefined>(props.modelValue ?? undefined);
const datepicker = ref(null);

const emit = defineEmits(["update:modelValue"]);

// Create a default validation context if none is provided
const defaultContext = {
  errors: new Map<string, string>(),
  validators: {} as Record<string, (key: string, validator: string) => void>,
  registerValidator: (key: string, validator: (key: string, validator: string) => void) => {
    defaultContext.validators[key] = validator;
  }
};

const context = inject(ValidationContextKey, defaultContext);
const { validateRequiredDateField, clearValidator, clearError, errors, registerValidator, hasError } = useFormValidation(context);
const control = ref();

const validateInput = () => {
  clearError(props.name);

  if (props.required) {
    validateRequiredDateField(props.name, props.label, innerValue.value ?? undefined);
  }
};

registerValidator(props.name, validateInput);

watch(
  () => props.modelValue,
  (newVal) => {
    innerValue.value =newVal;
    validateInput();
  }
);

watch(innerValue, (newVal) => {
  emit("update:modelValue", newVal);
});

if (props.modelValue && props.required) {
  validateInput();
}

const focus = () => {
  control.value.focus();
};

onBeforeUnmount(() => {
  clearValidator(props.name);
  clearError(props.name);
});

const inputDate = (inputEvent: InputEvent) => {
  validateInput();
  if (inputEvent.target && datepicker.value) {
    const target = inputEvent.target as HTMLInputElement;
    const date = new Date(target.value);
    const dateUtc = new Date(date.getTime() + date.getTimezoneOffset() * 60000);

    if (isNaN(date.getTime())) {
      console.error("Invalid date");
      innerValue.value = null;
      return;
    }

    if (dateUtc.getTime() !== innerValue.value?.getTime()) {
      // Adjust to local midnight
      dateUtc.setHours(0, 0, 0, 0);
      innerValue.value = dateUtc;
    }
  }
};

defineExpose({ focus });

// Add format prop if you want to customize the date format
const format = (date: Date) => {
  return date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric'
  });
};
</script>

<style scoped>
.datepicker-right :deep(.v3dp__popout) {
  right: 0;
  left: auto;
  margin-bottom:16px;
}
</style>

